import {
  alpha,
  Avatar,
  Box,
  Button,
  Card,
  Chip,
  Container,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useTheme
} from '@mui/material';
import { styled } from '@mui/material/styles';
import React, { useEffect, useReducer, useState } from "react";

// Icons
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AddIcon from '@mui/icons-material/Add';
import BlockIcon from '@mui/icons-material/Block';
import BusinessIcon from '@mui/icons-material/Business';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined';
import DevicesIcon from '@mui/icons-material/Devices';
import EditIcon from '@mui/icons-material/Edit';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import GroupIcon from '@mui/icons-material/Group';
import PersonIcon from '@mui/icons-material/Person';
import SearchIcon from '@mui/icons-material/Search';
import StorageIcon from '@mui/icons-material/Storage';
import VerifiedIcon from '@mui/icons-material/Verified';

// Componentes
import CreateOrEditCompanyModal from "../../components/CreateOrEditCompanyModal";
import CompanyUsersModal from "../../components/CompanyUsersModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import Page from '../../components/Page';
import Scrollbar from '../../components/Scrollbar';
import SearchNotFound from '../../components/SearchNotFound';
import TableRowSkeleton from "../../components/TableRowSkeleton";

// Serviços e utilidades
import useSettings from '../../hooks/useSettings';
import socket from "../../hooks/useSocket";
import api from "../../services/api";
import ToastError from "../../toast/error/toastError";
import ToastSuccess from "../../toast/success/toastSuccess";
import { i18n } from "../../translate/i18n";

/* ---------------------------------- ESTILOS ---------------------------------- */

// Configuração geral da página
const StyledContainer = styled(Container)(({ theme }) => ({
  paddingTop: theme.spacing(4),
  paddingBottom: theme.spacing(4),
  [theme.breakpoints.up('md')]: {
    paddingTop: theme.spacing(5),
    paddingBottom: theme.spacing(5),
  },
}));

const PageHeader = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(4),
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
  [theme.breakpoints.up('md')]: {
    marginBottom: theme.spacing(5),
  },
}));

// Cards de estatísticas
const StatsCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3, 3.5),
  height: '100%',
  borderRadius: 16,
  boxShadow: theme.palette.mode === 'dark'
    ? '0 2px 10px 0 rgba(0,0,0,0.25)'
    : '0 2px 10px 0 rgba(0,0,0,0.06)',
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  position: 'relative',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.palette.mode === 'dark'
      ? '0 8px 20px 0 rgba(0,0,0,0.3)'
      : '0 8px 20px 0 rgba(0,0,0,0.1)',
  },
}));

const IconContainer = styled(Box)(({ theme, color }) => ({
  width: 56,
  height: 56,
  borderRadius: 12,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: alpha(color || theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.2 : 0.12),
  color: color || theme.palette.primary.main,
  marginBottom: theme.spacing(2),
}));

const StatValue = styled(Typography)(({ theme }) => ({
  fontSize: 36,
  fontWeight: 700,
  lineHeight: 1.1,
  marginBottom: theme.spacing(0.5),
  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
}));

const StatTitle = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontWeight: 500,
  fontSize: 15,
}));

const DecorativeShape = styled(Box)(({ theme, color, right, bottom }) => ({
  position: 'absolute',
  width: 180,
  height: 180,
  borderRadius: '50%',
  bottom: bottom || -100,
  right: right || -80,
  background: `linear-gradient(135deg, ${alpha(color || theme.palette.primary.main, 0.08)} 0%, ${alpha(color || theme.palette.primary.light, 0.04)} 100%)`,
  zIndex: 0,
}));

// Tabela de empresas
const TableCard = styled(Card)(({ theme }) => ({
  borderRadius: 20,
  boxShadow: theme.palette.mode === 'dark'
    ? '0 4px 20px 0 rgba(0,0,0,0.25)'
    : '0 4px 20px 0 rgba(0,0,0,0.05)',
  overflow: 'hidden',
  marginTop: theme.spacing(4),
}));

const TableHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  padding: theme.spacing(3, 3, 2.5),
  gap: theme.spacing(2),
  flexWrap: 'wrap',
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
    alignItems: 'stretch',
    padding: theme.spacing(2.5, 2.5, 2),
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2, 2, 1.5),
  },
}));

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  boxShadow: 'none',
  borderRadius: 0,
  overflow: 'hidden',
}));

const TableHeaderCell = styled(TableCell)(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'dark'
    ? alpha(theme.palette.background.paper, 0.6)
    : alpha(theme.palette.background.neutral, 0.6),
  fontWeight: 600,
  color: theme.palette.text.primary,
  borderBottom: `1px solid ${theme.palette.divider}`,
  padding: theme.spacing(1.5, 2),
  fontSize: { xs: 11, sm: 12, md: 13 },
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  whiteSpace: 'nowrap',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1, 1),
    fontSize: 10,
  },
}));

const TableBodyCell = styled(TableCell)(({ theme }) => ({
  padding: theme.spacing(1.5, 2),
  borderBottom: `1px solid ${theme.palette.divider}`,
  fontSize: 14,
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1, 1),
  },
}));

// Formulários e controles
const SearchTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: 12,
    backgroundColor: theme.palette.mode === 'dark'
      ? alpha(theme.palette.common.white, 0.05)
      : alpha(theme.palette.common.black, 0.02),
    transition: 'all 0.2s ease-out',
    height: 48,
    fontSize: '0.875rem',
    '&:hover': {
      backgroundColor: theme.palette.mode === 'dark'
        ? alpha(theme.palette.common.white, 0.08)
        : alpha(theme.palette.common.black, 0.04),
    },
    '&.Mui-focused': {
      backgroundColor: theme.palette.mode === 'dark'
        ? alpha(theme.palette.common.white, 0.07)
        : theme.palette.common.white,
      boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.4 : 0.25)}`,
    },
    [theme.breakpoints.down('sm')]: {
      height: 44,
      fontSize: '0.813rem',
    },
  },
  '& .MuiOutlinedInput-input': {
    padding: theme.spacing(1.5, 1.75),
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(1.25, 1.5),
    },
  },
}));

const StyledFormControl = styled(FormControl)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: 12,
    backgroundColor: theme.palette.mode === 'dark'
      ? alpha(theme.palette.common.white, 0.05)
      : alpha(theme.palette.common.black, 0.02),
    height: 48,
    fontSize: '0.875rem',
    '&:hover': {
      backgroundColor: theme.palette.mode === 'dark'
        ? alpha(theme.palette.common.white, 0.08)
        : alpha(theme.palette.common.black, 0.04),
    },
    [theme.breakpoints.down('sm')]: {
      height: 44,
      fontSize: '0.813rem',
    },
  },
  '& .MuiSelect-select': {
    padding: theme.spacing(1.5, 1.75),
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(1.25, 1.5),
    },
  },
  '& .MuiInputLabel-root': {
    fontSize: '0.875rem',
    [theme.breakpoints.down('sm')]: {
      fontSize: '0.813rem',
    },
  },
}));

const ActionButton = styled(Button)(({ theme }) => ({
  borderRadius: 12,
  boxShadow: theme.shadows[0],
  padding: theme.spacing(1.5, 2),
  fontWeight: 600,
  textTransform: 'none',
  height: 48,
  fontSize: '0.875rem',
  transition: 'all 0.2s ease',
  '&:hover': {
    boxShadow: theme.shadows[4],
    transform: 'translateY(-1px)',
  },
  [theme.breakpoints.down('sm')]: {
    height: 44,
    fontSize: '0.813rem',
    padding: theme.spacing(1.25, 1.5),
  },
}));

// Componentes visuais
const StyledIconButton = styled(IconButton)(({ theme }) => ({
  borderRadius: 8,
  transition: 'all 0.2s',
  padding: 8,
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.08),
    transform: 'scale(1.05)',
  }
}));

const StatusChip = styled(Chip)(({ theme, status }) => {
  let color;
  let bgColor;

  switch (status) {
    case 'active':
      color = theme.palette.success.dark;
      bgColor = alpha(theme.palette.success.main, 0.12);
      break;
    case 'inactive':
      color = theme.palette.warning.dark;
      bgColor = alpha(theme.palette.warning.main, 0.12);
      break;
    case 'blocked':
      color = theme.palette.error.dark;
      bgColor = alpha(theme.palette.error.main, 0.12);
      break;
    case 'trial':
      color = theme.palette.info.dark;
      bgColor = alpha(theme.palette.info.main, 0.12);
      break;
    default:
      color = theme.palette.text.secondary;
      bgColor = alpha(theme.palette.grey[500], 0.12);
  }

  return {
    color: color,
    backgroundColor: bgColor,
    borderRadius: 6,
    fontWeight: 600,
    fontSize: { xs: 10, sm: 11 },
    border: `1px solid ${alpha(color, 0.24)}`,
    '& .MuiChip-icon': {
      color: color,
      fontSize: { xs: 14, sm: 16 },
    },
    padding: '0 4px',
    height: { xs: 22, sm: 24 },
  };
});

const CompanyAvatar = styled(Avatar)(({ theme, index }) => {
  // Array de cores para os avatares
  const colors = [
    theme.palette.primary.main,
    theme.palette.secondary.main,
    theme.palette.error.main,
    theme.palette.warning.main,
    theme.palette.info.main,
    theme.palette.success.main,
  ];

  const color = colors[index % colors.length];

  return {
    width: 40,
    height: 40,
    backgroundColor: alpha(color, 0.12),
    color: color,
    fontSize: 18,
    fontWeight: 'bold'
  };
});

const ProgressBar = styled(Box)(({ theme, value, max }) => {
  const percentage = (value / max) * 100;
  let color = theme.palette.success.main;
  if (percentage > 60) color = theme.palette.warning.main;
  if (percentage > 80) color = theme.palette.error.main;

  return {
    position: 'relative',
    width: '100%',
    height: 4,
    backgroundColor: alpha(theme.palette.divider, 0.5),
    borderRadius: 2,
    overflow: 'hidden',

    '&::after': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      width: `${percentage}%`,
      height: '100%',
      backgroundColor: color,
      transition: 'width 0.3s ease',
    }
  };
});

const UsageText = styled(Typography)(({ theme, value, max }) => {
  const percentage = (value / max) * 100;
  let color = theme.palette.success.main;
  if (percentage > 60) color = theme.palette.warning.main;
  if (percentage > 80) color = theme.palette.error.main;

  return {
    fontSize: 13,
    fontWeight: 600,
    color: color,
    marginLeft: theme.spacing(1)
  };
});

const SkeletonCard = styled(Skeleton)(({ theme }) => ({
  borderRadius: 16,
  transform: 'scale(1, 0.8)',
  transformOrigin: '0 0',
  marginTop: theme.spacing(1),
}));

/* ---------------------------------- REDUCER ---------------------------------- */

// Reducer para gerenciar o estado das empresas
const reducer = (state, action) => {
  switch (action.type) {
    case "LOAD_COMPANIES":
      const companies = action.payload;
      const newCompanies = [];

      companies.forEach((company) => {
        const companyIndex = state.findIndex((u) => u.id === company.id);
        if (companyIndex !== -1) {
          state[companyIndex] = company;
        } else {
          newCompanies.push(company);
        }
      });

      return [...state, ...newCompanies];

    case "UPDATE_COMPANIES":
      const company = action.payload;
      const companyIndex = state.findIndex((u) => u.id === company.id);

      if (companyIndex !== -1) {
        state[companyIndex] = company;
        return [...state];
      } else {
        return [company, ...state];
      }

    case "DELETE_COMPANIES":
      const companyId = action.payload;
      const deletedCompanyIndex = state.findIndex((u) => u.id === companyId);

      if (deletedCompanyIndex !== -1) {
        state.splice(deletedCompanyIndex, 1);
      }
      return [...state];

    case "RESET":
      return [];

    default:
      return state;
  }
};

/* ---------------------------------- HELPERS ---------------------------------- */

// Helpers para ordenação e filtragem
function descendingComparator(a, b, orderBy) {
  if (orderBy === 'createdAt') {
    return new Date(b.createdAt) - new Date(a.createdAt);
  }
  if (b[orderBy] < a[orderBy]) return -1;
  if (b[orderBy] > a[orderBy]) return 1;
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function applySortFilter(array, comparator, query) {
  const stabilizedThis = array.map((el, index) => [el, index]);

  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });

  if (query) {
    return array.filter((_company) =>
      _company.name.toLowerCase().indexOf(query.toLowerCase()) !== -1
    );
  }

  return stabilizedThis.map((el) => el[0]);
}

/* ---------------------------------- COMPONENTE PRINCIPAL ---------------------------------- */

const Company = () => {
  const theme = useTheme();
  const { themeStretch } = useSettings();

  // Estados
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState({});
  const [companies, dispatch] = useReducer(reducer, []);
  const [companyModalOpen, setCompanyModalOpen] = useState(false);
  const [companyUsersModalOpen, setCompanyUsersModalOpen] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [deletingCompany, setDeletingCompany] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState('desc');
  const [selected, setSelected] = useState([]);
  const [orderBy, setOrderBy] = useState('createdAt');
  const [filterName, setFilterName] = useState('');
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [status, setStatus] = useState(true);
  const [sortOrder, setSortOrder] = useState('newest');

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchCompany = async () => {
        try {
          const { data } = await api.get("/company/", {
            params: { searchParam, pageNumber, limit: rowsPerPage },
          });

          dispatch({ type: "LOAD_COMPANIES", payload: data?.companies || data });
          setHasMore(data.hasMore);
          setLoading(false);
        } catch (err) {
          ToastError(err);
          setLoading(false);
        }
      };
      fetchCompany();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, pageNumber, rowsPerPage]);

  useEffect(() => {
    socket.on("company", (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_COMPANIES", payload: data.company });
      }

      if (data.action === "delete") {
        dispatch({ type: "DELETE_COMPANIES", payload: +data.companyId });
      }
    });

    return () => {
      socket.off("company");
    };
  }, []);

  // Efeito para mudar a ordem quando o sortOrder mudar
  useEffect(() => {
    setOrderBy('createdAt');
    setOrder(sortOrder === 'newest' ? 'desc' : 'asc');
  }, [sortOrder]);

  // Handlers
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (e) => {
    setFilterName(e.target.value);
    setSearchParam(e.target.value);
  };

  const handleOpenCompanyModal = () => {
    setSelectedCompany(null);
    setCompanyModalOpen(true);
  };

  const handleCloseCompanyModal = () => {
    setSelectedCompany(null);
    setCompanyModalOpen(false);
  };

  const handleEditCompany = (id) => {
    setSelectedCompany(id);
    setCompanyModalOpen(true);
  };

  const handleOpenCompanyUsersModal = () => {
    setSelectedCompany(null);
    setCompanyUsersModalOpen(true);
  };

  const handleCloseCompanyUsersModal = () => {
    setSelectedCompany(null);
    setCompanyUsersModalOpen(false);
  };

  const handleEditCompanyUsers = (id) => {
    setSelectedCompany(id);
    setCompanyUsersModalOpen(true);
  };

  const handleChangeStatus = (event) => {
    setStatus(event.target.value);
  };

  const handleChangeSortOrder = (event) => {
    setSortOrder(event.target.value);
  };

  const handleDeleteCompany = async (companyId) => {
    try {
      if (!companyId) return;
      await api.delete(`/company/${companyId}`);
      ToastSuccess("Empresa excluída com sucesso");
    } catch (err) {
      ToastError(err);
    }
    setDeletingCompany(null);
    setSearchParam("");
    setPageNumber(1);
    setConfirmModalOpen(false);
  };

  const handleToggleActiveCompany = async (company) => {
    try {
      const newActiveStatus = !company.isActive;
      await api.put(`/company/${company.id}`, {
        ...company,
        isActive: newActiveStatus
      });

      ToastSuccess(
        newActiveStatus
          ? "Empresa desbloqueada com sucesso"
          : "Empresa bloqueada com sucesso"
      );

      // Atualizar a lista
      dispatch({
        type: "UPDATE_COMPANIES",
        payload: { ...company, isActive: newActiveStatus }
      });
    } catch (err) {
      ToastError(err);
    }
  };

  // Função para calcular há quanto tempo a empresa foi criada
  const getCompanyAge = (createdAt) => {
    const now = new Date();
    const created = new Date(createdAt);
    const diffTime = Math.abs(now - created);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 30) {
      return `${diffDays} ${i18n.t("companies.stats.days", { defaultValue: "dias" })}`;
    } else if (diffDays < 365) {
      const months = Math.floor(diffDays / 30);
      return `${months} ${i18n.t("companies.stats.months", { defaultValue: "meses" })}`;
    } else {
      const years = Math.floor(diffDays / 365);
      return `${years} ${i18n.t("companies.stats.years", { defaultValue: "anos" })}`;
    }
  };

  // Verificar se a empresa foi criada nos últimos 7 dias
  const isNewCompany = (createdAt) => {
    const now = new Date();
    const created = new Date(createdAt);
    const diffTime = Math.abs(now - created);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays <= 7;
  };

  // Dados para os cards de estatísticas
  const statsData = [
    {
      title: i18n.t("companies.pages.overview.companies", { defaultValue: "Empresas" }),
      value: overview.getCompanies || 0,
      icon: <BusinessIcon sx={{ fontSize: 28 }} />,
      color: theme.palette.primary.main,
      description: i18n.t("companies.stats.totalCompanies", { defaultValue: "Total de empresas cadastradas" })
    },
    {
      title: i18n.t("companies.pages.overview.users", { defaultValue: "Usuários" }),
      value: overview.getUsers || 0,
      icon: <GroupIcon sx={{ fontSize: 28 }} />,
      color: theme.palette.success.main,
      description: i18n.t("companies.stats.totalUsers", { defaultValue: "Usuários ativos na plataforma" })
    },
    {
      title: i18n.t("companies.pages.overview.connections", { defaultValue: "Conexões" }),
      value: overview.connections || 0,
      icon: <DevicesIcon sx={{ fontSize: 28 }} />,
      color: theme.palette.info.main,
      description: i18n.t("companies.stats.totalConnections", { defaultValue: "Conexões ativas no sistema" })
    }
  ];

  // Função auxiliar para formatar bytes em unidades legíveis
  const formatBytes = (bytes) => {
    if (!bytes || bytes === 0) return '0 B';

    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    const value = bytes / Math.pow(k, i);

    // Formatar com 2 casas decimais se for MB ou maior, senão sem decimais
    const formatted = i >= 2 ? value.toFixed(2) : Math.round(value);

    return `${formatted} ${sizes[i]}`;
  };

  // Função auxiliar para formatar a data e hora do último acesso
  const formatLastAccess = (latestUpdatedAt) => {
    if (!latestUpdatedAt) return null;

    const lastAccessDate = new Date(latestUpdatedAt);
    const now = new Date();
    const diffInMs = now - lastAccessDate;
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
    const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
    const diffInMinutes = Math.floor(diffInMs / (1000 * 60));

    // Data formatada completa
    const dateFormatted = lastAccessDate.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });

    const timeFormatted = lastAccessDate.toLocaleTimeString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit'
    });

    // Tempo relativo
    let timeRelative = '';
    if (diffInMinutes < 1) {
      timeRelative = 'agora mesmo';
    } else if (diffInMinutes < 60) {
      timeRelative = `há ${diffInMinutes} ${diffInMinutes === 1 ? 'minuto' : 'minutos'}`;
    } else if (diffInHours < 24) {
      timeRelative = `há ${diffInHours} ${diffInHours === 1 ? 'hora' : 'horas'}`;
    } else if (diffInDays < 30) {
      timeRelative = `há ${diffInDays} ${diffInDays === 1 ? 'dia' : 'dias'}`;
    } else if (diffInDays < 365) {
      const months = Math.floor(diffInDays / 30);
      timeRelative = `há ${months} ${months === 1 ? 'mês' : 'meses'}`;
    } else {
      const years = Math.floor(diffInDays / 365);
      timeRelative = `há ${years} ${years === 1 ? 'ano' : 'anos'}`;
    }

    return {
      dateFormatted,
      timeFormatted,
      timeRelative,
      diffInDays
    };
  };

  // Helpers para renderização
  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - companies.length) : 0;
  const filteredCompanies = applySortFilter(companies, getComparator(order, orderBy), filterName);
  const isNotFound = !filteredCompanies.length && Boolean(filterName);

  const getStatusChip = (isActive, isTrial, latestUpdatedAt) => {
    if (isTrial) {
      return (
        <StatusChip
          label={i18n.t("companies.status.trial", { defaultValue: "Teste" })}
          status="trial"
          size="small"
          icon={<VerifiedIcon sx={{ fontSize: { xs: '14px !important', sm: '16px !important' } }} />}
        />
      );
    }

    if (!isActive) {
      return (
        <StatusChip
          label={i18n.t("companies.status.blocked", { defaultValue: "Bloqueada" })}
          status="blocked"
          size="small"
          icon={<ErrorOutlineIcon sx={{ fontSize: { xs: '14px !important', sm: '16px !important' } }} />}
        />
      );
    }

    const accessInfo = formatLastAccess(latestUpdatedAt);

    if (accessInfo && accessInfo.diffInDays > 30) {
      return (
        <StatusChip
          label={i18n.t("companies.status.inactive", { defaultValue: "Inativa" })}
          status="inactive"
          size="small"
          icon={<AccessTimeIcon sx={{ fontSize: { xs: '14px !important', sm: '16px !important' } }} />}
        />
      );
    }

    return (
      <StatusChip
        label={i18n.t("companies.status.active", { defaultValue: "Ativa" })}
        status="active"
        size="small"
        icon={<VerifiedIcon sx={{ fontSize: { xs: '14px !important', sm: '16px !important' } }} />}
      />
    );
  };

  return (
    <Page title={i18n.t("companies.pages.overview.name")}>
      {/* Modais */}
      <ConfirmationModal
        title={
          deletingCompany &&
          `${i18n.t("companies.confirmations.delete")} ${deletingCompany.name}?`
        }
        open={confirmModalOpen}
        onClose={() => setConfirmModalOpen(false)}
        onConfirm={() => handleDeleteCompany(deletingCompany.id)}
      >
        {i18n.t("companies.deleteInfo")}
      </ConfirmationModal>

      <CompanyUsersModal
        open={companyUsersModalOpen}
        onClose={handleCloseCompanyUsersModal}
        aria-labelledby="form-dialog-title"
        companyId={selectedCompany}
      />

      <CreateOrEditCompanyModal
        open={companyModalOpen}
        onClose={handleCloseCompanyModal}
        aria-labelledby="form-dialog-title"
        companyId={selectedCompany}
      />

      <StyledContainer maxWidth={themeStretch ? false : 'xl'}>
        {/* Cabeçalho da Página */}
        <PageHeader>
          <Typography variant="h4" fontWeight={700} gutterBottom>
            {i18n.t("companies.pages.overview.name")}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {i18n.t("companies.pages.overview.description", { defaultValue: "Visão geral das empresas, usuários e conexões ativas no sistema." })}
          </Typography>
        </PageHeader>

        {/* Cards de Estatísticas */}
        <Grid container spacing={3}>
          {loading ? (
            // Estado de carregamento
            <>
              {[1, 2, 3].map((item) => (
                <Grid item xs={12} sm={6} md={4} key={item}>
                  <SkeletonCard variant="rectangular" height={160} />
                </Grid>
              ))}
            </>
          ) : (
            // Conteúdo carregado
            statsData.map((stat, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <StatsCard elevation={0}>
                  <Box sx={{ position: 'relative', zIndex: 1 }}>
                    <IconContainer color={stat.color}>
                      {stat.icon}
                    </IconContainer>
                    <StatValue>
                      {stat.value.toLocaleString()}
                    </StatValue>
                    <StatTitle gutterBottom>
                      {stat.title}
                    </StatTitle>
                    <Typography variant="caption" color="text.secondary">
                      {stat.description}
                    </Typography>
                  </Box>
                  <DecorativeShape color={stat.color} />
                </StatsCard>
              </Grid>
            ))
          )}
        </Grid>

        {/* Tabela de Empresas */}
        <TableCard elevation={0}>
          <TableHeader>
            <Box sx={{ flex: { xs: '1 1 100%', md: '0 1 auto' } }}>
              <Typography variant="h5" fontWeight={700} sx={{ fontSize: { xs: '1.125rem', sm: '1.25rem', md: '1.5rem' } }}>
                {i18n.t("companies.pages.overview.list", { defaultValue: "Lista de Empresas" })}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.688rem', sm: '0.75rem' }, display: { xs: 'none', sm: 'block' } }}>
                {i18n.t("companies.pages.overview.listDescription", { defaultValue: "Gerencie todas as empresas cadastradas no sistema" })}
              </Typography>
            </Box>

            {/* Filtros - Layout responsivo melhorado */}
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={{ xs: 1.5, md: 2 }}
              sx={{
                width: { xs: '100%', md: 'auto' },
                mt: { xs: 2, md: 0 },
                flex: { xs: '1 1 100%', md: '1 1 auto' },
                justifyContent: 'flex-end',
              }}
            >
              {/* Linha 1: Pesquisa em mobile / Pesquisa + Filtros em desktop */}
              <SearchTextField
                placeholder={i18n.t("companies.search.placeholder", { defaultValue: "Pesquisar..." })}
                value={filterName}
                onChange={handleSearchChange}
                size="small"
                sx={{
                  flex: { xs: '1 1 100%', md: '0 1 auto' },
                  minWidth: { xs: 0, md: 200 },
                  maxWidth: { xs: 'none', md: 300 }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: { xs: 18, sm: 20 } }} color="action" />
                    </InputAdornment>
                  ),
                }}
              />

              {/* Linha 2: Filtros em mobile / Continua na mesma linha em desktop */}
              <Stack
                direction="row"
                spacing={1.5}
                sx={{
                  width: { xs: '100%', md: 'auto' },
                }}
              >
                <StyledFormControl
                  size="small"
                  sx={{
                    flex: { xs: 1, md: 'initial' },
                    minWidth: { xs: 0, md: 110 },
                    maxWidth: { xs: 'none', md: 140 },
                  }}
                >
                  <InputLabel
                    id="status-select-label"
                    sx={{ fontSize: { xs: '0.813rem', sm: '0.875rem' } }}
                  >
                    {i18n.t("companies.filters.status", { defaultValue: "Status" })}
                  </InputLabel>
                  <Select
                    labelId="status-select-label"
                    id="status-select"
                    value={status}
                    label={i18n.t("companies.filters.status", { defaultValue: "Status" })}
                    onChange={handleChangeStatus}
                    sx={{ fontSize: { xs: '0.813rem', sm: '0.875rem' } }}
                  >
                    <MenuItem value={true} sx={{ fontSize: { xs: '0.813rem', sm: '0.875rem' } }}>
                      {i18n.t("companies.filters.active", { defaultValue: "Ativas" })}
                    </MenuItem>
                    <MenuItem value={false} sx={{ fontSize: { xs: '0.813rem', sm: '0.875rem' } }}>
                      {i18n.t("companies.filters.inactive", { defaultValue: "Inativas" })}
                    </MenuItem>
                    <MenuItem value={'trial'} sx={{ fontSize: { xs: '0.813rem', sm: '0.875rem' } }}>
                      {i18n.t("companies.filters.trial", { defaultValue: "Teste" })}
                    </MenuItem>
                  </Select>
                </StyledFormControl>

                <StyledFormControl
                  size="small"
                  sx={{
                    flex: { xs: 1, md: 'initial' },
                    minWidth: { xs: 0, md: 130 },
                    maxWidth: { xs: 'none', md: 180 },
                  }}
                >
                  <InputLabel
                    id="sort-select-label"
                    sx={{ fontSize: { xs: '0.813rem', sm: '0.875rem' } }}
                  >
                    {i18n.t("companies.filters.sortBy", { defaultValue: "Ordenar" })}
                  </InputLabel>
                  <Select
                    labelId="sort-select-label"
                    id="sort-select"
                    value={sortOrder}
                    label={i18n.t("companies.filters.sortBy", { defaultValue: "Ordenar" })}
                    onChange={handleChangeSortOrder}
                    sx={{ fontSize: { xs: '0.813rem', sm: '0.875rem' } }}
                  >
                    <MenuItem value="newest" sx={{ fontSize: { xs: '0.813rem', sm: '0.875rem' } }}>
                      {i18n.t("companies.filters.newest", { defaultValue: "Mais recentes" })}
                    </MenuItem>
                    <MenuItem value="oldest" sx={{ fontSize: { xs: '0.813rem', sm: '0.875rem' } }}>
                      {i18n.t("companies.filters.oldest", { defaultValue: "Mais antigas" })}
                    </MenuItem>
                  </Select>
                </StyledFormControl>

                {/* Botão adicionar - sempre por último */}
                <ActionButton
                  variant="contained"
                  color="primary"
                  startIcon={<AddIcon sx={{ display: { xs: 'none', sm: 'block' } }} />}
                  onClick={handleOpenCompanyModal}
                  sx={{
                    minWidth: { xs: 48, sm: 'auto' },
                    px: { xs: 1.5, sm: 2 },
                    fontSize: { xs: '0.813rem', sm: '0.875rem' },
                    whiteSpace: 'nowrap',
                  }}
                >
                  <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                    {i18n.t("companies.buttons.add", { defaultValue: "Nova" })}
                  </Box>
                  <AddIcon sx={{ display: { xs: 'block', sm: 'none' }, fontSize: 20 }} />
                </ActionButton>
              </Stack>
            </Stack>
          </TableHeader>

          <Divider />

          <Scrollbar>
            <StyledTableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableHeaderCell align="left" sx={{ minWidth: { xs: 120, sm: 180 } }}>
                      {i18n.t("companies.table.name", { defaultValue: "Empresa" })}
                    </TableHeaderCell>
                    <TableHeaderCell align="center" sx={{ display: { xs: 'none', lg: 'table-cell' }, minWidth: 120 }}>
                      {i18n.t("companies.table.cnpj", { defaultValue: "CNPJ" })}
                    </TableHeaderCell>
                    <TableHeaderCell align="center" sx={{ minWidth: { xs: 90, sm: 110 } }}>
                      {i18n.t("companies.table.usage", { defaultValue: "Uso" })}
                    </TableHeaderCell>
                    <TableHeaderCell align="center" sx={{ display: { xs: 'none', md: 'table-cell' }, minWidth: 140 }}>
                      {i18n.t("companies.table.lastAccess", { defaultValue: "Último Acesso" })}
                    </TableHeaderCell>
                    <TableHeaderCell align="center" sx={{ minWidth: { xs: 80, sm: 100 } }}>
                      {i18n.t("companies.table.status", { defaultValue: "Status" })}
                    </TableHeaderCell>
                    <TableHeaderCell align="center" sx={{ minWidth: { xs: 120, sm: 140 } }}>
                      {i18n.t("companies.table.action", { defaultValue: "Ações" })}
                    </TableHeaderCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {loading && !companies.length ? (
                    <TableRowSkeleton columns={6} rows={5} />
                  ) : (
                    (filteredCompanies.filter(company => {
                      if (status === 'trial') return company?.isTrial;
                      return company?.isActive === status;
                    }) || []).slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row, index) => {
                      const { id, name, cnpj, maxWhatsapps, createdAt, maxUsers, latestUpdatedAt, totalConnections: whatsappUsage, connectedConnections, totalUsers: userUsage, total_size, isActive, isTrial } = row;
                      const isNew = isNewCompany(createdAt);
                      const isItemSelected = selected.indexOf(name) !== -1;

                      return (
                        <TableRow
                          hover
                          key={id}
                          tabIndex={-1}
                          role="checkbox"
                          selected={isItemSelected}
                          aria-checked={isItemSelected}
                          sx={{
                            '&:hover': { backgroundColor: theme.palette.action.hover },
                            bgcolor: isNew ? alpha(theme.palette.info.main, 0.04) : 'inherit'
                          }}
                        >
                          <TableBodyCell align="left">
                            <Stack direction="row" alignItems="center" spacing={{ xs: 1, sm: 2 }}>
                              <CompanyAvatar index={index} sx={{ width: { xs: 32, sm: 40 }, height: { xs: 32, sm: 40 }, fontSize: { xs: 14, sm: 18 } }}>
                                {name.charAt(0).toUpperCase()}
                              </CompanyAvatar>
                              <Box sx={{ minWidth: 0, flex: 1 }}>
                                <Stack direction="row" alignItems="center" spacing={0.5} flexWrap="wrap">
                                  <Typography variant="subtitle2" noWrap fontWeight={600} sx={{ fontSize: { xs: '0.813rem', sm: '0.875rem' } }}>
                                    {name}
                                  </Typography>
                                  {isNew && (
                                    <Chip
                                      label="Nova"
                                      color="info"
                                      size="small"
                                      sx={{ fontSize: '9px', height: 16, '& .MuiChip-label': { px: 0.5 } }}
                                    />
                                  )}
                                </Stack>
                                <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'none', sm: 'block' }, fontSize: '0.688rem' }}>
                                  ID: {id}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'block', sm: 'none' }, fontSize: '0.688rem' }}>
                                  #{id}
                                </Typography>
                              </Box>
                            </Stack>
                          </TableBodyCell>

                          <TableBodyCell align="center" sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                            <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace', fontSize: '0.813rem' }}>
                              {cnpj && cnpj.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, "$1.$2.$3/$4-$5")}
                            </Typography>
                          </TableBodyCell>

                          <TableBodyCell align="center">
                            <Box sx={{ minWidth: { xs: 90, sm: 110 } }}>
                              <Stack spacing={0.5} alignItems="center">
                                {/* Conexões */}
                                <Tooltip
                                  title={
                                    <Box sx={{ textAlign: 'center' }}>
                                      <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                        Conexões WhatsApp
                                      </Typography>
                                      <Typography variant="caption" sx={{ display: 'block', mb: 0.25 }}>
                                        <Box component="span" sx={{ color: 'success.light', fontWeight: 700 }}>
                                          {connectedConnections || 0}
                                        </Box>
                                        {' '}conectadas no momento
                                      </Typography>
                                      <Typography variant="caption" sx={{ display: 'block', mb: 0.25 }}>
                                        <Box component="span" sx={{ fontWeight: 600 }}>
                                          {whatsappUsage || 0}
                                        </Box>
                                        {' '}criadas no total
                                      </Typography>
                                      <Typography variant="caption" sx={{ display: 'block' }}>
                                        Limite: {maxWhatsapps || 0} conexões
                                      </Typography>
                                    </Box>
                                  }
                                  arrow
                                  placement="top"
                                >
                                  <Stack direction="row" alignItems="center" spacing={0.5} justifyContent="center" sx={{ cursor: 'help' }}>
                                    <DevicesIcon sx={{ fontSize: { xs: 14, sm: 16 } }} color="primary" />
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                                      <Typography
                                        variant="caption"
                                        sx={{
                                          fontSize: { xs: '0.688rem', sm: '0.813rem' },
                                          fontWeight: 700,
                                          color: (connectedConnections || 0) > 0 ? 'success.main' : 'text.secondary',
                                        }}
                                      >
                                        {connectedConnections || 0}
                                      </Typography>
                                      <Typography variant="caption" sx={{ fontSize: { xs: '0.688rem', sm: '0.813rem' }, color: 'text.secondary' }}>
                                        /
                                      </Typography>
                                      <UsageText value={whatsappUsage || 0} max={maxWhatsapps || 1} sx={{ fontSize: { xs: '0.688rem', sm: '0.813rem' } }}>
                                        {whatsappUsage || 0}/{maxWhatsapps || 0}
                                      </UsageText>
                                    </Box>
                                  </Stack>
                                </Tooltip>
                                <ProgressBar value={whatsappUsage || 0} max={maxWhatsapps || 1} sx={{ height: 3 }} />

                                {/* Usuários */}
                                <Tooltip
                                  title={
                                    <Box sx={{ textAlign: 'center' }}>
                                      <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                        Usuários
                                      </Typography>
                                      <Typography variant="caption" sx={{ display: 'block', mb: 0.25 }}>
                                        <Box component="span" sx={{ fontWeight: 600 }}>
                                          {userUsage || 0}
                                        </Box>
                                        {' '}usuários criados
                                      </Typography>
                                      <Typography variant="caption" sx={{ display: 'block' }}>
                                        Limite: {maxUsers || 0} usuários
                                      </Typography>
                                    </Box>
                                  }
                                  arrow
                                  placement="top"
                                >
                                  <Stack direction="row" alignItems="center" spacing={0.5} mt={0.5} sx={{ cursor: 'help' }}>
                                    <PersonIcon sx={{ fontSize: { xs: 14, sm: 16 } }} color="info" />
                                    <UsageText value={userUsage || 0} max={maxUsers || 1} sx={{ fontSize: { xs: '0.688rem', sm: '0.813rem' } }}>
                                      {userUsage || 0}/{maxUsers || 0}
                                    </UsageText>
                                  </Stack>
                                </Tooltip>
                                <ProgressBar value={userUsage || 0} max={maxUsers || 1} sx={{ height: 3 }} />

                                {/* Uso de Disco */}
                                <Tooltip
                                  title={
                                    <Box sx={{ textAlign: 'center' }}>
                                      <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                        Armazenamento em Disco
                                      </Typography>
                                      <Typography variant="caption" sx={{ display: 'block' }}>
                                        Total utilizado: {formatBytes(total_size)}
                                      </Typography>
                                    </Box>
                                  }
                                  arrow
                                  placement="top"
                                >
                                  <Stack direction="row" alignItems="center" spacing={0.5} mt={0.5} sx={{ cursor: 'help' }}>
                                    <StorageIcon sx={{ fontSize: { xs: 14, sm: 16 } }} color="warning" />
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        fontSize: { xs: '0.688rem', sm: '0.813rem' },
                                        fontWeight: 600,
                                        color: 'warning.main',
                                      }}
                                    >
                                      {formatBytes(total_size)}
                                    </Typography>
                                  </Stack>
                                </Tooltip>
                              </Stack>
                            </Box>
                          </TableBodyCell>

                          <TableBodyCell align="center" sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                            {latestUpdatedAt ? (
                              <Box>
                                <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.813rem' }}>
                                  {formatLastAccess(latestUpdatedAt)?.timeRelative || 'N/A'}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.688rem' }}>
                                  {formatLastAccess(latestUpdatedAt)?.dateFormatted}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.688rem' }}>
                                  {formatLastAccess(latestUpdatedAt)?.timeFormatted}
                                </Typography>
                              </Box>
                            ) : (
                              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.813rem' }}>
                                Sem registro
                              </Typography>
                            )}
                          </TableBodyCell>

                          <TableBodyCell align="center">
                            <Stack spacing={0.5} alignItems="center">
                              {getStatusChip(isActive, isTrial, latestUpdatedAt)}
                              <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'block', md: 'none' }, fontSize: '0.688rem' }}>
                                {latestUpdatedAt && formatLastAccess(latestUpdatedAt)?.timeRelative}
                              </Typography>
                            </Stack>
                          </TableBodyCell>

                          <TableBodyCell align="center">
                            <Stack direction="row" spacing={{ xs: 0.5, sm: 1 }} justifyContent="center" flexWrap="wrap">
                              <Tooltip title={i18n.t("companies.actions.users", { defaultValue: "Usuários" })}>
                                <StyledIconButton
                                  onClick={() => handleEditCompanyUsers(id)}
                                  sx={{
                                    color: theme.palette.info.main,
                                    padding: { xs: 0.5, sm: 1 }
                                  }}
                                  size="small"
                                >
                                  <GroupIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />
                                </StyledIconButton>
                              </Tooltip>

                              <Tooltip title={i18n.t("companies.actions.edit", { defaultValue: "Editar" })}>
                                <StyledIconButton
                                  onClick={() => handleEditCompany(id)}
                                  sx={{
                                    color: theme.palette.warning.main,
                                    padding: { xs: 0.5, sm: 1 }
                                  }}
                                  size="small"
                                >
                                  <EditIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />
                                </StyledIconButton>
                              </Tooltip>

                              <Tooltip title={isActive ? "Bloquear empresa" : "Desbloquear empresa"}>
                                <StyledIconButton
                                  onClick={() => handleToggleActiveCompany(row)}
                                  sx={{
                                    color: isActive ? theme.palette.warning.dark : theme.palette.success.main,
                                    padding: { xs: 0.5, sm: 1 }
                                  }}
                                  size="small"
                                >
                                  {isActive ? (
                                    <BlockIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />
                                  ) : (
                                    <CheckCircleIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />
                                  )}
                                </StyledIconButton>
                              </Tooltip>

                              <Tooltip title={i18n.t("companies.actions.delete", { defaultValue: "Excluir" })}>
                                <StyledIconButton
                                  onClick={() => {
                                    setConfirmModalOpen(true);
                                    setDeletingCompany(row);
                                  }}
                                  sx={{
                                    color: theme.palette.error.main,
                                    padding: { xs: 0.5, sm: 1 }
                                  }}
                                  size="small"
                                >
                                  <DeleteOutlineIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />
                                </StyledIconButton>
                              </Tooltip>
                            </Stack>
                          </TableBodyCell>
                        </TableRow>
                      );
                    })
                  )}

                  {emptyRows > 0 && (
                    <TableRow style={{ height: 53 * emptyRows }}>
                      <TableCell colSpan={6} />
                    </TableRow>
                  )}
                </TableBody>

                {isNotFound && !loading && (
                  <TableBody>
                    <TableRow>
                      <TableCell align="center" colSpan={6} sx={{ py: 3 }}>
                        <SearchNotFound searchQuery={filterName} />
                      </TableCell>
                    </TableRow>
                  </TableBody>
                )}
              </Table>
            </StyledTableContainer>
          </Scrollbar>

          <TablePagination
            component="div"
            count={filteredCompanies.filter(company => {
              if (status === 'trial') return company?.isTrial;
              return company?.isActive === status;
            }).length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(e, page) => setPage(page)}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[10, 25, 50, 100]}
            labelDisplayedRows={({ from, to, count }) => {
              // Versão compacta para mobile
              const isMobile = window.innerWidth < 600;
              return isMobile
                ? `${from}-${to} / ${count}`
                : `${i18n.t("userType.translate.result", { defaultValue: "Resultados" })} ${from}-${to} ${i18n.t("userType.translate.of", { defaultValue: "de" })} ${count}`;
            }}
            labelRowsPerPage={i18n.t("userType.translate.resultsPerPage", { defaultValue: "Por página" })}
            sx={{
              borderTop: (theme) => `1px solid ${theme.palette.divider}`,
              '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                marginBottom: 0,
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
              },
              '& .MuiTablePagination-select': {
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
              },
              '& .MuiTablePagination-actions': {
                marginLeft: { xs: 1, sm: 2 },
              },
            }}
          />
        </TableCard>
      </StyledContainer>
    </Page>
  );
}

export default Company;